/**
 * English dictionary — key tree must exactly match the zh-HK baseline (enforced by i18n:check).
 */
import type { Dict } from '../index';

const dict: Dict = {
  // Site meta
  'meta.title': 'JKD Coding | Using No Way as Way — AI Business Consultant',
  'meta.description': 'JKD Coding fuses Bruce Lee\'s Jeet Kune Do philosophy to serve NGOs, social enterprises, and SMEs with AI conversion diagnostics, growth system design, and customer relationship operations.',

  // Navbar
  'nav.arsenal': 'Arsenal',
  'nav.components': 'Components',
  'nav.custom': 'Custom',
  'nav.philosophy': 'Philosophy',
  'nav.consult': 'Free Consult',

  // Hero
  'hero.kicker': 'Be Water, My Friend',
  'hero.titleA': 'Be Water, Be AI',
  'hero.titleB': 'Solve Business Problems Fast',
  'hero.subtitle': 'JKD Coding designs AI conversion systems for NGOs, social enterprises, and SMEs — never forcing technology, only finding the most natural form for your business: donor growth, retention, and cash-flow early warning in one place.',
  'hero.cta': 'Book AI Diagnostic',
  'hero.approach': 'Our Approach',
  'hero.quote': 'Empty your mind, be formless, shapeless, like water.',

  // Services
  'services.tag': 'Our Practice',
  'services.title': 'Not Tools. Solutions.',
  'services.subtitle': 'Every engagement starts with your business bottleneck, then we shape AI to fit.',
  'services.m1.eyebrow': '01 / Growth',
  'services.m1.title': 'Donor & Customer Growth',
  'services.m1.problem': 'Visitors arrive but do not convert, and acquisition costs keep rising.',
  'services.m1.solution': 'Design AI-driven conversion funnels that turn every interaction into a lasting relationship.',
  'services.m1.result': 'Higher conversion, lower acquisition cost',
  'services.m2.eyebrow': '02 / Relationships',
  'services.m2.title': 'Customer Relationship Operations',
  'services.m2.problem': 'Follow-ups slip through the cracks and clients gradually go cold.',
  'services.m2.solution': 'Turn one-to-one communication into a predictable, scalable relationship operation.',
  'services.m2.result': 'Less churn, higher retention',
  'services.m3.eyebrow': '03 / Risk',
  'services.m3.title': 'Cash Flow & Risk Early Warning',
  'services.m3.problem': 'Bad debt and competitor switching are only noticed after the fact.',
  'services.m3.solution': 'AI monitors behavioral signals daily and alerts you before risks materialize.',
  'services.m3.result': 'Protect cash flow, act early',
  'services.m4.eyebrow': '04 / Adoption',
  'services.m4.title': 'Change Adoption & Efficiency',
  'services.m4.problem': 'Systems go live, but staff resist using them and training costs pile up.',
  'services.m4.solution': 'Design adoption paths that start from existing habits so change feels natural.',
  'services.m4.result': 'Higher adoption, fewer errors',
  'services.m5.eyebrow': '05 / KNOWLEDGE',
  'services.m5.problem': 'The meeting ends, and the experience evaporates with it.',
  'services.problemLabel': 'The Problem',
  'services.solutionLabel': 'Our Approach',
  'services.cta': 'Book a no-pressure diagnostic',

  // Arsenal
  'arsenal.tag': 'READY-TO-DEPLOY',
  'arsenal.title': 'Arsenal',
  'arsenal.subtitle': 'Scenario-based solutions, rapid deployment, battle-ready',
  'arsenal.blindbox.title': 'Blind Box System',
  'arsenal.blindbox.subtitle': 'Seasonal Marketing Tool',
  'arsenal.blindbox.desc': 'Probability engine, prize management, social viral mechanisms, analytics dashboard. Perfect for NGO fundraising, e-commerce promotions, KOL engagement.',
  'arsenal.blindbox.f1': 'Seasonal themes (CNY/Christmas/Mid-Autumn)',
  'arsenal.blindbox.f2': 'Custom probability & guarantee mechanism',
  'arsenal.blindbox.f3': 'Social share unlock features',
  'arsenal.blindbox.f4': 'Complete analytics dashboard',
  'arsenal.blindbox.f5': '30-min video support',
  'arsenal.whatsapp.title': 'WhatsApp Custom Auto-Follow System',
  'arsenal.whatsapp.subtitle': 'Customer Relationship Engine',
  'arsenal.whatsapp.desc': 'Flexible pre-configured workflows: abandoned cart recovery, order updates, appointment confirmations, satisfaction surveys.',
  'arsenal.whatsapp.f1': 'Message template library',
  'arsenal.whatsapp.f2': 'Chatbot configuration',
  'arsenal.whatsapp.f3': 'CRM system integration',
  'arsenal.whatsapp.f4': 'Deploy in 30 minutes',
  'arsenal.whatsapp.f5': 'Ongoing technical support',
  'arsenal.promise': '10 mins to own your conversion funnel',
  'arsenal.priceFrom': 'From',

  // Products
  'products.tag': 'NEW RELEASES',
  'products.title': 'AI Business Tools',
  'products.subtitle': 'AI business assistants designed for SMEs. Experience the interactive demo now.',
  'products.cta': 'Experience Now',
  'products.landlink.title': 'Landlink',
  'products.landlink.subtitle': 'AI Habit Adoption Assistant',
  'products.landlink.desc': 'Solves the "system is live, but staff/customers won\'t adopt it" problem. AI reads existing habits and proactively prepares the most common next steps, turning multi-step workflows into one.',
  'products.landlink.f1': 'Reads existing habits to prepare the next step',
  'products.landlink.f2': 'Management adoption summary + daily WhatsApp digest',
  'products.landlink.f3': 'Frontline points & one-click repeat actions',
  'products.landlink.f4': 'Turns 5 steps into 1, cutting training and error costs',
  'products.bizpulse.title': 'Bizpulse',
  'products.bizpulse.subtitle': 'AI Client Risk Monitor',
  'products.bizpulse.desc': 'Monitors client behavior 24/7, spotting late payments, churn, and competitor switching risks early. Act before bad debt happens and protect your cash flow.',
  'products.bizpulse.f1': 'AI daily client record review',
  'products.bizpulse.f2': 'Auto-detects slower payments & cooling engagement',
  'products.bizpulse.f3': 'Instant risk alert notifications',
  'products.bizpulse.f4': 'No AI skills needed, ready out of the box',

  // Components
  'components.tag': 'COMPONENTS LIBRARY',
  'components.title': 'Components',
  'components.subtitle': 'Modular capabilities, combine on demand, technical autonomy',
  'components.quote': '"Thinking is the problem, doing is the answer"',
  'components.scroll.name': 'Scroll Animation Module',
  'components.scroll.tech': 'Intersection Observer API',
  'components.scroll.desc': 'Supports 12 scroll-triggered effects — fade-in, slide-in, zoom, rotation — with configurable thresholds and delays',
  'components.i18n.name': 'Bilingual i18n Framework',
  'components.i18n.tech': 'URL Params / Browser Detection',
  'components.i18n.desc': 'Content switching driven by URL parameters or browser language auto-detection, with RTL support reserved',
  'components.cf-track.name': 'Cloudflare Workers Tracking',
  'components.cf-track.tech': 'Serverless',
  'components.cf-track.desc': 'Serverless user behavior tracking with custom events, audience segmentation, and parallel GA4 tracking',
  'components.cf-webhook.name': 'Cloudflare Workers Webhook',
  'components.cf-webhook.tech': 'HTTP Endpoints',
  'components.cf-webhook.desc': 'Standardized HTTP endpoints with request validation, rate limiting, and Zapier/Make integration',
  'components.og.name': 'OG Tag Optimization Kit',
  'components.og.tech': 'Dynamic Meta Tags',
  'components.og.desc': 'Dynamic Open Graph tag generation with A/B testing and social media preview simulation',
  'components.full.title': 'Full Components Library',
  'components.full.desc': 'All components + priority support + quarterly updates. Ideal for mid-to-large projects requiring systematic technical capabilities.',
  'components.full.tag1': 'All Components',
  'components.full.tag2': 'Priority Support',
  'components.full.tag3': 'Quarterly Updates',
  'components.perYear': '/yr',
  'components.orMonthly': 'or HK$200/month',

  // Custom
  'custom.tag': 'CUSTOM DOJO',
  'custom.title': 'Custom Dojo',
  'custom.subtitle': 'Fully customized conversion funnels, from zero to one, creating your unique conversion machine',
  'custom.price': 'Project Quote',
  'custom.priceNote': 'Customized based on scope and complexity',
  'custom.ctaNote': 'No commitment, understand your business needs first',
  'custom.phase1.week': 'Week 1',
  'custom.phase1.title': 'Competitor Research & Opportunity Analysis',
  'custom.phase1.desc': 'Systematic analysis of 5-10 top competitors: heatmap reverse engineering, performance scoring, SEO keyword overlap',
  'custom.phase1.deliverable': 'Conversion Gap Analysis Report',
  'custom.phase2.week': 'Week 1–2',
  'custom.phase2.title': 'Copy Strategy & Information Architecture',
  'custom.phase2.desc': 'Jobs-to-be-Done user interviews, 10 headline variants, social proof strategy, CTA optimization',
  'custom.phase2.deliverable': 'Copy Strategy Manual',
  'custom.phase3.week': 'Week 2–4',
  'custom.phase3.title': 'UI/UX Design & Prototype Validation',
  'custom.phase3.desc': 'Dual-track sprint mode, Figma high-fidelity interactive prototype, real device testing',
  'custom.phase3.deliverable': 'Clickable Prototype + Design Specs',
  'custom.phase4.week': 'Week 4–6',
  'custom.phase4.title': 'Technical Development & Deployment',
  'custom.phase4.desc': 'User Interface (UI) layer construction, JSON logic configuration, Cloudflare Workers edge deployment',
  'custom.phase4.deliverable': 'Production Website',
  'custom.phase5.week': 'Week 6–8',
  'custom.phase5.title': 'Data Tracking & Optimization',
  'custom.phase5.desc': 'GA4 traffic analysis, Hotjar behavior heatmaps, A/B testing framework, custom conversion dashboard',
  'custom.phase5.deliverable': 'Data Monitoring Report',
  'custom.cta': 'Book Free Consultation',

  // Philosophy
  'philosophy.tag': 'PHILOSOPHY',
  'philosophy.title': 'Philosophy',
  'philosophy.subtitle': 'Vibe-coding methodology, teaching you to fish rather than giving you fish',
  'philosophy.pillar1.title': 'Jeet Kune Do Philosophy',
  'philosophy.pillar1.desc': 'Bruce Lee\'s "using no way as way" philosophy meets modern agile development, exploring how martial arts wisdom transforms into digital development mindset',
  'philosophy.pillar2.title': 'RPE Methodology Framework',
  'philosophy.pillar2.desc': 'Rapid Prototyping & Execution complete manual: requirement interviews, prototype tool selection, user testing, iteration rhythm',
  'philosophy.pillar3.title': 'Tool Reviews & Practice',
  'philosophy.pillar3.desc': 'In-depth reviews of Bubble, Webflow, Framer and other no-code/low-code platforms. No sugarcoating, building objective authority',

  // FAQ
  'faq.tag': 'FAQ',
  'faq.title': 'Frequently Asked Questions',
  'faq.subtitle': 'Direct answers about how JKD Coding works',
  'faq.q1': 'What kinds of organisations do you serve?',
  'faq.a1': 'We serve NGOs, social enterprises, and SMEs worldwide. Whether it is donor growth, customer relationship operations, or cash-flow early warning, we always start from your real business problem.',
  'faq.q2': 'What is the AI conversion diagnostic, and what happens in it?',
  'faq.a2': 'It is a 30-minute one-to-one consultant conversation. We clarify your biggest business bottleneck and find the most natural entry point for AI; afterwards you receive concrete improvement recommendations, with zero sales pressure.',
  'faq.q3': 'How is pricing calculated?',
  'faq.a3': 'Ready-made solutions start at HK$800 (Blind Box System) and HK$1,500 (WhatsApp auto-follow); individual components cost HK$300–800, the full library HK$2,000/year; fully custom projects are quoted by scope, typically HK$15,000–50,000.',
  'faq.q4': 'How fast can we go live?',
  'faq.a4': 'Ready-made solutions deploy in as fast as 30 minutes; the component library is delivered within days; custom projects run through five phases and typically go live in 6–8 weeks.',
  'faq.q5': 'We are not technical — can we still work with you?',
  'faq.a5': 'Absolutely. We handle the technical details; you only need to describe your business goals. All inquiries are answered within 24 hours, and ongoing technical support follows once a plan is confirmed.',
  'faq.author': 'Author: JKD Coding Team',
  'faq.updated': 'Updated September 2026',

  // Contact
  'contact.tag': 'GET IN TOUCH',
  'contact.title': 'Start Your Journey',
  'contact.subtitle': '"Life... is just like poop, sometimes soft, sometimes hard; sometimes smooth, sometimes not!"',
  'contact.form.title': 'Send Message',
  'contact.form.name': 'Name',
  'contact.form.namePlaceholder': 'Your name',
  'contact.form.email': 'Email',
  'contact.form.emailPlaceholder': 'your@email.com',
  'contact.form.business': 'Business Type',
  'contact.form.businessPlaceholder': 'Select your business type',
  'contact.form.businessNgo': 'NGO / Social Enterprise',
  'contact.form.businessSme': 'SME',
  'contact.form.businessPersonal': 'Personal Brand',
  'contact.form.businessOther': 'Other',
  'contact.form.message': 'Message',
  'contact.form.messagePlaceholder': 'Briefly describe your needs or questions...',
  'contact.form.submit': 'Send Message',
  'contact.form.processing': 'Processing...',
  'contact.form.waNote': 'The next step verifies your identity via WhatsApp — please make sure you can use WhatsApp.',
  'contact.verify.title': 'Verify Your WhatsApp',
  'contact.verify.desc': 'Click the button below to open WhatsApp and send the verification message to complete your submission.',
  'contact.verify.codeLabel': '你的驗證碼 Your Verification Code',
  'contact.verify.openWa': '打開 WhatsApp 驗證 / Open WhatsApp',
  'contact.verify.prefillNote': '驗證訊息將自動填入 Verification message will be pre-filled',
  'contact.verify.back': '← 返回修改資料 / Go Back',
  'contact.success.title': 'Verification Successful!',
  'contact.success.desc': 'We have received your inquiry and will reply within 24 hours.',
  'contact.quick.title': 'Quick Contact',
  'contact.quick.whatsapp': 'WhatsApp Consultation',
  'contact.quick.whatsappDesc': 'Instant reply, book free consultation',
  'contact.quick.response': 'Quick Response Promise',
  'contact.quick.responseDesc': 'All inquiries answered within 24 hours',
  'contact.promise': '"Whether you\'re an NGO, social enterprise or SME, we\'ll find the most direct way to help you discover the best solution. Don\'t worry about technical details, just tell us your business goals."',
  'contact.promiseAuthor': '— JKD Coding Team',
  'contact.alertFail': 'Submission failed. Please try again or reach us directly on WhatsApp.',
  'contact.waPrefill': 'Hello! I would like to learn more about JKD Coding\'s services.',

  // Footer
  'footer.desc': 'Fusing Bruce Lee\'s Jeet Kune Do philosophy with "using no way as way" as core innovative thinking, building conversion funnels for NGOs, social enterprises, and SMEs.',
  'footer.products': 'Products',
  'footer.content': 'Content',
  'footer.about': 'About',
  'footer.aboutUs': 'About Us',
  'footer.contact': 'Contact',
  'footer.faq': 'FAQ',
  'footer.privacy': 'Privacy Policy',
  'footer.terms': 'Terms of Service',
  'footer.rights': 'All rights reserved.',
  // Diagnostic share card (P0 referral entry)
  'share.title': 'Refer a Friend',
  'share.desc': 'Know a business struggling with system adoption? Share the free diagnostic with them.',
  'share.copy': 'Copy link',
  'share.copied': 'Copied!',
  'share.whatsapp': 'Share via WhatsApp',
  'share.more': 'More ways to share',
  'share.prefill': "A friend recommended I try JKD Coding's free AI conversion diagnostic (30 minutes, zero sales pressure):",

  // Diagnostic page (/diagnosis)
  'diag.tag': '30-min Strategy Call',
  'diag.title': 'AI Conversion Diagnostic',
  'diag.subtitle': '30 minutes to clarify your business bottleneck and find the most natural entry point for AI.',
  'diag.back': 'Back to Home',
  'diag.step1Title': 'Tell us about your situation',
  'diag.step1Desc': 'After submitting, our consultant will contact you within 1 business day.',
  'diag.name': 'Name *',
  'diag.organization': 'Organization *',
  'diag.role': 'Role *',
  'diag.email': 'Email *',
  'diag.businessType': 'Business Type *',
  'diag.businessPlaceholder': 'Select',
  'diag.ngo': 'NGO / Social Enterprise',
  'diag.sme': 'SME',
  'diag.personal': 'Personal Brand',
  'diag.other': 'Other',
  'diag.challenge': 'What is your biggest conversion challenge? *',
  'diag.challengePlaceholder': 'Select',
  'diag.challenge1': 'Visitors arrive but do not convert',
  'diag.challenge2': 'Follow-ups slip through or response is too slow',
  'diag.challenge3': 'Bad debt or churn risk is hard to predict',
  'diag.challenge4': 'Team resists using the new system',
  'diag.challenge5': 'Not enough content to keep communicating',
  'diag.goals': 'Business goal you want to solve first *',
  'diag.goalsPlaceholder': 'Briefly describe the outcome you want, e.g. increase donation completion by 30%',
  'diag.budget': 'Budget range (optional)',
  'diag.budgetPlaceholder': 'Select',
  'diag.budget1': 'HK$8,000 – 20,000',
  'diag.budget2': 'HK$20,000 – 50,000',
  'diag.budget3': 'HK$50,000 – 150,000',
  'diag.budget4': 'HK$150,000+',
  'diag.privacy': 'Your information is only used to arrange this conversation and will never be resold.',
  'diag.submit': 'Book My Diagnostic',
  'diag.submitting': 'Processing...',
  'diag.verifyTitle': 'Verify Your WhatsApp',
  'diag.verifyDesc': 'Click the button below to open WhatsApp and send the verification message to complete your booking.',
  'diag.code': 'Your Verification Code',
  'diag.openWhatsApp': 'Open WhatsApp to Verify',
  'diag.prefilled': 'Verification message will be pre-filled',
  'diag.goBack': 'Go Back',
  'diag.successTitle': 'Verification Successful!',
  'diag.successDesc': 'We have received your diagnostic request and will reply within 24 hours.',
  'diag.alertFail': 'Submission failed. Please try again.',
  'diag.msgOrg': 'Organization',
  'diag.msgRole': 'Role',
  'diag.msgChallenge': 'Biggest challenge',
  'diag.msgGoals': 'Goal to solve first',
  'diag.msgBudget': 'Budget range',
  'diag.msgUnfilled': 'Not provided',
  // Landlink (/landlink)
  'landlink.brandName': 'Landlink',
  'landlink.backAria': 'Back to home',
  'landlink.brandAria': 'Landlink',
  'landlink.zero.kicker': 'AI Habit Adoption Assistant',
  'landlink.zero.titleA': 'The system is live,',
  'landlink.zero.titleB': 'but staff or customers won\'t use it?',
  'landlink.zero.body': 'Landlink reads existing routines and preps the most common tasks,',
  'landlink.zero.bodyStrong': 'turning five steps into one.',
  'landlink.zero.cta': 'See where the friction is',
  'landlink.zero.dashLabel': 'ADOPTION RATE',
  'landlink.one.kicker': 'AI is unpacking your adoption friction',
  'landlink.one.title': 'Tech goes live; habits may not.',
  'landlink.one.body': 'Tap the 3 signals to see why staff or customers avoid the system.',
  'landlink.one.closeAria': 'Close',
  'landlink.one.continueNext': 'Continue to the next signal →',
  'landlink.one.continueDone': 'See how AI takes over →',
  'landlink.one.progressPre': 'Signals viewed:',
  'landlink.one.progressPost': '',
  'landlink.one.f1.eyebrow': 'Friction: Familiarity',
  'landlink.one.f1.title': 'The old way costs zero thought',
  'landlink.one.f1.text': 'Paper and pen are already instinct; even one extra step in a new system gets abandoned at the busiest moment. The problem isn\'t features — it\'s the cost of switching habits.',
  'landlink.one.f2.eyebrow': 'Friction: Fear of mistakes',
  'landlink.one.f2.title': 'Frontline staff fear pressing wrong',
  'landlink.one.f2.text': 'When outcomes aren\'t clear, frontline staff take the safest old path. What they need isn\'t another training session — it\'s instant, certain feedback.',
  'landlink.one.f3.eyebrow': 'Friction: No payoff',
  'landlink.one.f3.title': 'Change has nothing to do with me',
  'landlink.one.f3.text': 'The company gains efficiency; frontline staff only feel the hassle. Payoff has to land on the individual before adoption turns from an order into a choice.',
  'landlink.two.kicker': 'AI agents aren\'t a concept — watch one actually work',
  'landlink.two.titlePre': '5 steps into ',
  'landlink.two.titleHighlight': '1 step',
  'landlink.two.titlePost': '.',
  'landlink.two.body': 'Same order, two ways. The gap is the choice staff and customers face every day.',
  'landlink.two.startHint': 'Press start to watch AI compress five steps into one action using existing habits.',
  'landlink.two.startCta': 'Start the comparison',
  'landlink.two.classicLabel': 'The old way',
  'landlink.two.classicStepsLabel': '5 steps',
  'landlink.two.step1': 'Open the order system',
  'landlink.two.step2': 'Select each item',
  'landlink.two.step3': 'Enter quantity & specs',
  'landlink.two.step4': 'Check delivery details',
  'landlink.two.step5': 'Confirm & send',
  'landlink.two.orderLabel': 'Sample order',
  'landlink.two.orderItem': '🥬 Bok choy × 2',
  'landlink.two.aiLabel': 'The AI way',
  'landlink.two.aiStepsLabel': '1 action',
  'landlink.two.aiReadyLabel': 'AI has it ready',
  'landlink.two.aiOrder': '🥬 Bok choy × 2 · to the Central branch · this morning',
  'landlink.two.evidenceIntro': 'Based on past habits',
  'landlink.two.ev1.label': 'Last 4 orders',
  'landlink.two.ev1.value': 'All bok choy × 2',
  'landlink.two.ev2.label': 'Usual delivery spot',
  'landlink.two.ev2.value': 'Central branch · 9 times',
  'landlink.two.ev3.label': 'Monday habit',
  'landlink.two.ev3.value': 'Usually restocks at 10am',
  'landlink.two.confirm': 'Same again · Confirm & send',
  'landlink.two.confirmed': 'Confirmed',
  'landlink.two.aiNote': 'AI only uses company records it\'s authorized to see — every suggestion has a source.',
  'landlink.three.titlePre': 'Not asking people to change —',
  'landlink.three.titleMid': '',
  'landlink.three.titleHighlight': 'making it worth it',
  'landlink.three.titlePost': '.',
  'landlink.three.body': 'Motivation, ability, and prompts are all required. Landlink lowers friction and raises payoff for management and frontline at once.',
  'landlink.three.cta': 'Start the conversation',
  'landlink.three.card1.title': 'Management layer | Driven top-down',
  'landlink.three.card1.copy': 'Gives managers visibility of adoption progress and a basis for daily proactive reminders.',
  'landlink.three.card2.title': 'Frontline layer | Incentives land on the individual',
  'landlink.three.card2.copy': 'Makes the new system faster, easier, and worth opening every day.',
  'landlink.four.kicker': 'Make the system actually land',
  'landlink.four.titlePre': 'The next one to change',
  'landlink.four.titleMid': 'could be',
  'landlink.four.titleHighlight': 'your system',
  'landlink.four.titlePost': '.',
  'landlink.four.body': 'Tell us the system you\'re rolling out and we\'ll find the step worth doing first.',
  'landlink.four.ctaLoading': 'Connecting you to a consultant…',
  'landlink.four.cta': 'Learn more on WhatsApp',
  'landlink.four.note': 'No tech skills needed · we walk you through it',
  'landlink.four.waPrefill': 'Hello, I\'d like to learn about Landlink, the AI habit adoption assistant.',

  // Bizpulse (/bizpulse)
  'bizpulse.brandName': 'Bizpulse',
  'bizpulse.backAria': 'Back to home',
  'bizpulse.title': 'Hire an AI risk officer that never slacks',
  'bizpulse.kicker': 'AI client risk monitoring',
  'bizpulse.heroBody': 'Watching every client for you, 24/7.',
  'bizpulse.heroStrong': 'Late payments, churn, or defection — AI tells you first.',
  'bizpulse.heroCta': 'See how AI watches my clients',
  'bizpulse.scanLabel': 'AI is scanning your clients',
  'bizpulse.one.title': 'AI sees risk before it happens.',
  'bizpulse.one.body': 'Tap the 3 red alerts to see what AI spots early.',
  'bizpulse.one.closeAria': 'Close',
  'bizpulse.one.continueNext': 'Continue to the next alert →',
  'bizpulse.one.continueDone': 'See how the AI risk officer works →',
  'bizpulse.one.progressPre': 'Alerts viewed:',
  'bizpulse.one.progressPost': '',
  'bizpulse.one.s1.title': 'Possible late payment',
  'bizpulse.one.s1.tag': 'AI noticed: payments slowing',
  'bizpulse.one.s1.detail': 'This client\'s payment dates keep slipping. AI alerts you before it becomes bad debt so you can chase early and protect cash flow.',
  'bizpulse.one.s2.title': 'Possible churn',
  'bizpulse.one.s2.tag': 'AI noticed: client going cold',
  'bizpulse.one.s2.detail': 'Fewer inquiries, slower replies, less engagement. AI monitors daily so you can retain before they leave.',
  'bizpulse.one.s3.title': 'Possible defection',
  'bizpulse.one.s3.tag': 'AI noticed: ordering habits changed',
  'bizpulse.one.s3.detail': 'Order frequency and volume are off pattern. AI surfaces these subtle shifts so you can grab the last follow-up window.',
  'bizpulse.two.step1.title': 'Connect records',
  'bizpulse.two.step1.copy': 'One place for client data',
  'bizpulse.two.step2.title': 'Daily AI scans',
  'bizpulse.two.step2.copy': 'Spot abnormal behavior',
  'bizpulse.two.step3.title': 'Instant alerts',
  'bizpulse.two.step3.copy': 'Tells you the risk directly',
  'bizpulse.two.step4.title': 'Follow up early',
  'bizpulse.two.step4.copy': 'Protect business and cash',
  'bizpulse.two.finalSmall': 'Your AI risk officer',
  'bizpulse.two.titlePre': 'No AI skills needed.',
  'bizpulse.two.titleHighlight': 'AI watches your clients automatically.',
  'bizpulse.two.finalBody': 'Scans daily, never slacks; the moment there\'s risk, you\'re notified.',
  'bizpulse.two.ctaLoading': 'Connecting you to a consultant…',
  'bizpulse.two.cta': 'Learn more on WhatsApp',
  'bizpulse.two.note': 'No tech skills needed · we walk you through it',
  'bizpulse.two.waPrefill': 'Hello, I\'d like to know how to hire an AI risk officer to monitor my clients.',

  // Promo (/promo)
  'promo.kicker': 'Real estate × social media automation',
  'promo.titlePre': 'Listing updates, posts, analytics —',
  'promo.titleHighlight': 'one system does it all.',
  'promo.heroLead': 'The moment a new listing lands, it becomes a professional FB post in 10 seconds — automatic watermarking, tracked short links, and stats on which listing draws the most interest.',
  'promo.heroBold': '"Photos to video"',
  'promo.heroTail': ' — minimum effort, maximum client experience.',
  'promo.videoCaption': '▲ Live demo: property data → one-click professional video (auto music, subtitles, contact card, QR)',
  'promo.ctaDemo': 'Book a demo on WhatsApp',
  'promo.ctaTry': 'Try it online',
  'promo.waPrefill': 'I\'d like to learn about the listing-social sync solution',
  'promo.painTitle': 'Three things every agent faces daily',
  'promo.pain1.t': 'Listings change daily',
  'promo.pain1.d': 'New, rented, sold — after updating the website, you post on socials manually too, running both directions',
  'promo.pain2.t': 'Photos stolen once online',
  'promo.pain2.d': 'Hard-shot listing photos get downloaded and reused in no time — even AI edits can\'t stop it',
  'promo.pain3.t': 'Ad spend pours away',
  'promo.pain3.d': 'Posts go out with no idea which listing gets views or clicks — without data there\'s no direction',
  'promo.stepsTitlePre': 'From listing to post —',
  'promo.stepsTitleHighlight': '3 steps, 10 seconds',
  'promo.stepsNote': 'Real system screenshots below, not concept art',
  'promo.screenshotAlt': 'Live system view: social promotion area',
  'promo.screenshotCaption': '▲ Backend live: select photos → auto-generated copy (editable) → one-click publish straight to FB',
  'promo.step1.t': 'Pick photos',
  'promo.step1.d': 'Select property photos; the system adds a centered diagonal brand watermark — hard to remove even with AI editing',
  'promo.step2.t': 'Post',
  'promo.step2.d': 'Post copy auto-generated: title, sale/rent price, address, short link — tap "Copy text" to edit',
  'promo.step3.t': 'See the numbers',
  'promo.step3.d': 'Every post ships with tracking: FB clicks, property-page views — all visible in the dashboard',
  'promo.magicBadge': 'The killer feature',
  'promo.magicTitlePre': 'A few photos become',
  'promo.magicTitleHighlight': 'a professional listing video.',
  'promo.magicBodyLead': 'Upload listing photos and the system generates a 1080p video: auto music, dynamic subtitles (title/price/address), brand logo, ',
  'promo.magicBodyBold': 'zero editing skills, zero manpower',
  'promo.magicBodyTail': '. Video posts reach far beyond photo posts on FB — one listing, two ways to play it.',
  'promo.bullet1': 'Photo carousel + Ken Burns motion',
  'promo.bullet2': 'Four music styles, one-tap switch',
  'promo.bullet3': 'Square 1:1 / vertical 9:16, works on FB & IG',
  'promo.compareTitle': 'Let\'s do the math',
  'promo.compare.col1': 'Hiring / manual',
  'promo.compare.col2': 'This system',
  'promo.compare.r1.c0': 'One post',
  'promo.compare.r1.c1': '30–60 minutes',
  'promo.compare.r1.c2': '10 seconds',
  'promo.compare.r2.c0': 'A listing video',
  'promo.compare.r2.c1': 'Half a day + editing fees',
  'promo.compare.r2.c2': '1–2 minutes, auto-generated',
  'promo.compare.r3.c0': 'Photo protection',
  'promo.compare.r3.c1': 'None',
  'promo.compare.r3.c2': 'Built-in AI-grade watermark',
  'promo.compare.r4.c0': 'Per-listing analytics',
  'promo.compare.r4.c1': 'None / separate tools',
  'promo.compare.r4.c2': 'Built in, dashboard-ready',
  'promo.compare.r5.c0': 'Monthly cost',
  'promo.compare.r5.c1': '$8,000–15,000',
  'promo.compare.r5.c2': 'One affordable flat fee',
  'promo.compareNote': '* The system is running live with property agents today',
  'promo.ctaTitlePre': 'Next listing that lands,',
  'promo.ctaTitleHighlight': 'it\'s on FB 10 seconds later.',
  'promo.ctaSub': 'Book a 15-minute online demo and try it with your own listing.',
  'promo.ctaNote': 'Cloud-based · you own your data · no lock-in · dedicated support',
  'promo.footer': 'JKD Coding · Using No Way as Way',

  // ===== Distill Knowledge Distillery (/distill/) =====
  'distill.meta.title': 'The Knowledge Distillery — Turn organisational experience into rules that work｜JKD Coding',
  'distill.meta.description': 'The JKD Coding Knowledge Distillery helps enterprises and SMEs extract knowledge: turning experience scattered across meetings, emails and veteran colleagues into executable, reusable rules that AI employees can act on. The moment the meeting ends, the rules are on the wall.',
  'distill.hero.kicker': 'Be Water, My Friend',
  'distill.hero.title': 'Knowledge is water — it takes shape when it is contained',
  'distill.hero.tagline': 'THE KNOWLEDGE DISTILLERY',
  'distill.hero.lead': 'JKD Coding provides knowledge extraction for large enterprises and SMEs: we distil the experience scattered across meetings, emails and the minds of veteran colleagues into executable, reusable rules that your AI employees can act on — the moment the meeting ends, the rules are on the wall.',
  'distill.hero.cta1': 'Book a 30-minute diagnosis',
  'distill.hero.cta2': 'The four-step method',
  'distill.pain.quote.en': 'Absorb what is useful, discard what is useless.',
  'distill.pain.quote.zh': 'Absorb what is useful, discard what is useless — Bruce Lee defined knowledge extraction decades ago.',
  'distill.pain.heading': 'Four ways organisational knowledge evaporates',
  'distill.pain.c1.tt': 'The veteran retires',
  'distill.pain.c1.ds': 'Twenty years of judgement gone with a farewell party — the experience never left his head.',
  'distill.pain.c2.tt': 'The minutes graveyard',
  'distill.pain.c2.ds': 'Stored, but never read again. The four-thousandth file sits deep in a shared drive.',
  'distill.pain.c3.tt': 'Fossilised SOPs',
  'distill.pain.c3.ds': 'Outdated the moment they are written — documents trail reality, and new hires learn the wrong way.',
  'distill.pain.c4.tt': 'The hollow AI',
  'distill.pain.c4.ds': 'Without your knowledge, an AI is just a polite chatbot.',
  'distill.method.quote.en': "It's not the daily increase but daily decrease — hack away at the unessential.",
  'distill.method.quote.zh': 'Not daily increase but daily decrease — distillation is the art of cutting. A rule wall is carved, not hoarded.',
  'distill.method.heading': 'The four-step distillation loop',
  'distill.method.s1.tt': 'Intercept',
  'distill.method.s1.en': '01 / INTERCEPT',
  'distill.method.s1.ds': 'The day a meeting ends, its record goes straight into the still — before it evaporates.',
  'distill.method.s1.out': 'Output: raw knowledge captured',
  'distill.method.s2.tt': 'Distil',
  'distill.method.s2.en': '02 / DISTIL',
  'distill.method.s2.ds': 'AI and consultant cut together: AI refines, the consultant strips away the unessential.',
  'distill.method.s2.out': 'Output: candidate rules',
  'distill.method.s3.tt': 'Crystallise',
  'distill.method.s3.en': '03 / CRYSTALLISE',
  'distill.method.s3.ds': 'Human review puts rules on the wall — each one: context + action + owner.',
  'distill.method.s3.out': 'Output: the rule wall',
  'distill.method.s4.tt': 'Reflow',
  'distill.method.s4.en': '04 / REFLOW',
  'distill.method.s4.ds': 'AI employees work by the rules; monthly reviews sharpen them with use.',
  'distill.method.s4.out': 'Output: organisational memory',
  'distill.show.kicker': 'THE DISTILLERY · FEATURE',
  'distill.show.heading': 'The journey of a single drop',
  'distill.show.lead': 'Up top, the raw noise that falls every day; the single gold line in the middle is the hand that intercepts it; below, the rules that stay and do the work.',
  'distill.show.ch1': 'meeting-notes_v7_final(2).docx',
  'distill.show.ch2': 'The 38th cross-department meeting',
  'distill.show.ch3': '"He said we\'d discuss it later…"',
  'distill.show.ch4': 'Shared drive (4,200 files)',
  'distill.show.ch5': 'The veteran retires next month',
  'distill.show.ch6': '217 emails back and forth',
  'distill.show.funnel': 'INTERCEPT · DISTIL · SETTLE',
  'distill.show.rule1.tag': 'RULE · MEETINGS',
  'distill.show.rule1.text': 'Every meeting ends with "who + does what + by when" — missing any one, it reconvenes.',
  'distill.show.rule2.tag': 'RULE · EXTERNAL',
  'distill.show.rule2.text': 'Any commitment is confirmed internally before it goes out — no one speaks for the company off the cuff.',
  'distill.show.rule3.tag': 'RULE · DOCUMENTS',
  'distill.show.rule3.text': 'Operating procedures are reviewed every 90 days — the writer signs, the reviser leaves a trace.',
  'distill.show.rule4.tag': 'RULE · HANDOVER',
  'distill.show.rule4.text': 'A new hire reads the rule wall within three days, then starts work.',
  'distill.show.note': 'Sample rules are generic illustrations, not any client\'s actual rule library',
  'distill.proof.kicker': 'PROOF OF PRACTICE',
  'distill.proof.heading': 'We don\'t teach distillation — we distil every day',
  'distill.proof.s1.n': '252',
  'distill.proof.s1.l': 'working rules continuously distilled in our own governance system',
  'distill.proof.s2.n': 'Weekly',
  'distill.proof.s2.l': 'harvest-and-review rhythm, never skipped',
  'distill.proof.s3.n': '6 languages',
  'distill.proof.s3.l': 'site-wide knowledge-governance experience, directly transferable',
  'distill.proof.quote.en': 'Using no way as way — having no limitation as limitation.',
  'distill.proof.quote.zh': 'Using no way as way — rules are distilled from real practice, not copied from templates.',
  'distill.fit.heading': 'Who this is for',
  'distill.fit.f1.tt': 'NGOs',
  'distill.fit.f1.ds': 'Donor relationships and volunteer experience stop leaving with every staff rotation.',
  'distill.fit.f2.tt': 'Social enterprises',
  'distill.fit.f2.ds': 'Mission and operating experience carried through growth without deforming.',
  'distill.fit.f3.tt': 'SMEs',
  'distill.fit.f3.ds': 'Turn the owner\'s judgement into an institution the whole company can use.',
  'distill.fit.f4.tt': 'Large enterprises',
  'distill.fit.f4.ds': 'Cross-department knowledge governance: each team\'s tacit experience becomes executable rules for AI employees.',
  'distill.faq.q1': 'How long does distillation take?',
  'distill.faq.a1': 'The first round targets same-day delivery: the day a meeting ends, its rules are on the wall. The full loop — intercept, distil, crystallise, reflow — advances weekly by organisation size, then keeps cycling through monthly reviews.',
  'distill.faq.q2': 'Will our knowledge leak?',
  'distill.faq.a2': 'No. The rules we distil belong to you and live inside your jurisdiction; we handle them only during the engagement with your authorisation, and step away after delivery.',
  'distill.faq.q3': 'How is this different from AI knowledge-management tools?',
  'distill.faq.a3': 'Tools store; distillation cuts and shapes. Without the "who + does what + by when" rule structure, more data is still unexecutable — we deliver judgement, not a database.',
  'distill.faq.q4': 'How do distilled rules connect to AI employees?',
  'distill.faq.a4': 'The rule wall is what an AI employee works from: the AI project manager acts by the rules and confirms internally by the rules, and monthly reviews feed new experience back as new rules — a closed loop.',
  'distill.faq.q5': 'Does the approach differ between large enterprises and SMEs?',
  'distill.faq.a5': 'SMEs focus on institutionalising the owner\'s judgement; large enterprises need cross-department governance — each team\'s tacit experience unified into non-conflicting, executable rule sets.',
  'distill.faq.q6': 'How do we start?',
  'distill.faq.a6': 'Start with a 30-minute diagnosis: inventory your organisation\'s most valuable — and most at-risk (evaporating) — knowledge assets, then decide the scope and rhythm of distillation.',
  'distill.funnel.heading': 'From a drop of noise to a team of AI employees',
  'distill.funnel.f1.tag': 'STEP 1',
  'distill.funnel.f1.tt': 'Diagnosis',
  'distill.funnel.f1.ds': 'A 30-minute one-on-one to inventory your organisation\'s most valuable — and most at-risk (evaporating) — knowledge assets.',
  'distill.funnel.f2.tag': 'STEP 2 · THIS PAGE',
  'distill.funnel.f2.tt': 'Knowledge distillation',
  'distill.funnel.f2.ds': 'The four-step loop puts rules on the wall — knowledge finally has a shape.',
  'distill.funnel.f3.tag': 'STEP 3',
  'distill.funnel.f3.tt': 'Managed AI employees',
  'distill.funnel.f3.ds': 'AI project managers on site, working from your rule wall — we sell employees, not software.',
  'distill.cta1': 'Talk on WhatsApp',
  'distill.cta2': 'About diagnosis',
  'distill.wa.text': 'Hi! I\u2019d like to learn about JKD Coding\u2019s Knowledge Distillery service.',
  'distill.footer': 'JKD Coding · Using no way as way',
  'distill.card.title': 'Knowledge extraction',
  'distill.card.desc': 'Distil experience scattered across meetings and veteran colleagues into executable rules your AI employees can act on.',
  'distill.card.cta': 'Enter the Distillery',
  'distill.xs.title': 'After diagnosis: distil findings into rules',
  'distill.xs.body': 'The diagnosis finds the bottleneck; the Knowledge Distillery turns organisational experience into executable, reusable rules your AI employees can act on — the moment the meeting ends, the rules are on the wall.',
  'distill.xs.cta': 'Enter the Distillery',

  // ============ Terms & Conditions /terms (3 tabs: Privacy / Refund / Delivery) ============
  'terms.meta.title': 'Terms & Conditions — Privacy, Refund & Delivery Policy | JKD Coding',
  'terms.meta.description': 'Terms & Conditions of JKD Coding (operated by Wiseman Consultancy Limited): Privacy Policy, Refund Policy and Delivery Policy, applying to this website and all consulting and digital development services.',
  'terms.title': 'Terms & Conditions',
  'terms.subtitle': 'These terms apply to the JKD Coding website (jkdcoding.com) and all consulting and digital services we provide. The website and services are operated by Wiseman Consultancy Limited.',
  'terms.updated': 'Last updated: 2026-10-13',
  'terms.tab.privacy': 'Privacy Policy',
  'terms.tab.refund': 'Refund Policy',
  'terms.tab.delivery': 'Delivery Policy',
  'terms.contact': 'If you have any questions, reach us on WhatsApp +852 5165 1480.',

  // — Privacy Policy —
  'terms.privacy.note': 'This policy explains how JKD Coding (operated by **Wiseman Consultancy Limited**) collects, uses and protects your data. By using this website or our services you agree to these terms.',
  'terms.privacy.h1': '1. Data We Collect',
  'terms.privacy.s1': '- **Enquiry & contact data**: name, WhatsApp number, email, organisation (provided via diagnosis booking, WhatsApp or forms)\n- **Project data**: business material, knowledge-base content and website assets you provide\n- **Browsing data**: page views, referrers and marketing parameters (anonymous statistics used to improve the website and ad performance)\n- **Payment data**: we retain only invoice numbers and amounts, and **never see or store your payment instrument details**',
  'terms.privacy.h2': '2. How We Use Data',
  'terms.privacy.s2': '- Providing, operating and improving our consulting and development services\n- Quotations, project management, technical support and billing\n- Essential service notices (booking confirmations, delivery arrangements, security alerts)',
  'terms.privacy.h3': '3. Third-party Processing (AI Engine)',
  'terms.privacy.s3': 'To deliver AI-related services, content you provide may be transmitted to the large language model providers we use (including DeepSeek, Z.ai and others), which process data under their own privacy policies. We only transmit the content needed to complete the work, and we continuously review their data protection arrangements.',
  'terms.privacy.h4': '4. Data Retention',
  'terms.privacy.s4': '- Enquiry and conversation records: retained for approximately 24 months, then purged\n- Project files: retained for 90 days after delivery for fixes and support, then deleted on request or automatically purged',
  'terms.privacy.h5': '5. What We Never Do',
  'terms.privacy.s5': '- We never sell or rent your data\n- We never use your — or your customers\u2019 — data for third-party marketing\n- We never disclose data to third parties except where required by law or necessary to provide the service (e.g. AI engines, hosting and payment providers)',
  'terms.privacy.h6': '6. Your Rights',
  'terms.privacy.s6': 'You may access, correct or delete your personal data. Send your request via WhatsApp +852 5165 1480; we respond within **14 days**. Our handling of personal data complies with the Personal Data (Privacy) Ordinance (Cap. 486, Laws of Hong Kong).',
  'terms.privacy.h7': '7. Policy Updates',
  'terms.privacy.s7': 'Material changes will be published on this page with an updated date. Changes that materially affect the use of the service will be notified separately.',

  // — Refund Policy —
  'terms.refund.note': 'JKD Coding provides digital consulting and development services (no physical goods). All prices are in HKD. We encourage you to book our **free diagnosis** (30-minute one-on-one) first and decide with clarity before paying.',
  'terms.refund.h1': '1. Free Diagnosis',
  'terms.refund.s1': 'The diagnosis is entirely free with no sales pressure. All paid work is quoted after the diagnosis and begins only after your written confirmation.',
  'terms.refund.h2': '2. Cancellation Before Work Begins',
  'terms.refund.s2': 'If you cancel after confirmation but before work begins: all payments are **refunded in full**. Third-party costs already incurred on your behalf (domain, licences, cloud resources, etc.) are deducted at cost against receipts.',
  'terms.refund.h3': '3. Ready-made Plans & Component Library',
  'terms.refund.s3': 'Within **7 days** of payment, if the service fails to be delivered or operate due to our fault and we cannot fix it within a reasonable time, you may request a **full refund**. Once successfully delivered and operating normally, the service is deemed complete and non-refundable.',
  'terms.refund.h4': '4. Custom Projects (Milestone-based)',
  'terms.refund.s4': '- Custom projects are billed per milestone: **started milestones** are settled at actual completion progress; **unstarted milestones** are refundable\n- Mid-term cancellation: completed and accepted work is non-refundable; the remainder is refunded pro-rata\n- Change requests after delivery are new scope and quoted separately',
  'terms.refund.h5': '5. Billing Errors',
  'terms.refund.s5': 'Duplicate or incorrect charges, once verified, will be fully refunded to the original payment method within **7 business days**.',
  'terms.refund.h6': '6. How to Request',
  'terms.refund.s6': 'WhatsApp +852 5165 1480 (please provide your invoice number). All requests are processed within **7 business days**; refunds are returned via the original payment channel.',

  // — Delivery Policy —
  'terms.delivery.note': 'JKD Coding provides purely digital services — **no physical shipping** is involved. All delivery is electronic; no shipping fees apply.',
  'terms.delivery.h1': '1. Delivery Timeline',
  'terms.delivery.s1': '- **Ready-made plans**: deployment as fast as 30 minutes after requirements are confirmed and payment received (business hours)\n- **Component library**: licences and documentation sent by email/WhatsApp within 1 business day of payment\n- **Custom projects**: delivered per contractual milestones, typically live in 6\u20138 weeks\n- Requests outside business hours are handled in the next business session; all enquiries are answered within 24 hours',
  'terms.delivery.h2': '2. What Is Delivered',
  'terms.delivery.s2': 'Depending on the plan, delivery generally includes: access to the website/system, source code or deployment environment, setup and usage guides, and the technical support contact channel.',
  'terms.delivery.h3': '3. Delivery Method',
  'terms.delivery.s3': 'Everything is delivered electronically (web, email, WhatsApp). Once deployment is complete and both parties have accepted the work, the service is deemed delivered and commenced.',
  'terms.delivery.h4': '4. Delivery Delays',
  'terms.delivery.s4': 'If delivery exceeds the promised timeline, you may choose **prompt re-delivery** or request a refund under the [Refund Policy](#refund).',

  // — Footer terms link —
  'footer.termsLink': 'Terms & Conditions',
};

export default dict;
